import React, { useEffect, useRef, useContext, useMemo } from "react"
import { mapLocale } from "../utils/mapping"
import { LocaleContext } from "../context/context"

const isProduction =
    process.env.NODE_ENV === "production" &&
    typeof window !== "undefined" &&
    /^(digitup\.com|www\.digitup\.com)$/.test(window.location.hostname)

const Scripts = ({ isLandingPage = false }) => {
    const hasLoadedRef = useRef(false)
    const { locale } = useContext(LocaleContext)
    const localePrefix = useMemo(() => mapLocale[locale] || "in/en", [locale])

    // GTM - Locale-based Script IDs loading over both prodution staging/localhost
    const GTM_IDS = {
        "in/en": process.env.GATSBY_GTM_ID_IN_EN,
        "jp/ja": process.env.GATSBY_GTM_ID_JP_JA,
        "mena/ar": process.env.GATSBY_GTM_ID_MENA_AR,
        "mena/en": process.env.GATSBY_GTM_ID_MENA_EN,
        "uk/en": process.env.GATSBY_GTM_ID_UK_EN,
        lp: process.env.GATSBY_GTM_ID_LP,
    }

    // RUM - Script IDs loading on production only
    const RUM_SITE_ID = process.env.GATSBY_RUM_SITE_ID
    const RUM_SERVER = process.env.GATSBY_RUM_SERVER_URL
    const RUM_BRAND = process.env.GATSBY_RUM_BRAND

    const loadGTM = ({ localePrefix, isLandingPage }) => {
        if (document.getElementById("gtm")) return

        const gtmId = isLandingPage
            ? GTM_IDS["lp"]
            : GTM_IDS[localePrefix] || GTM_IDS["in/en"]
        if (!gtmId) return

        const script = document.createElement("script")
        script.id = "gtm"
        script.defer = true
        script.async = true
        script.innerHTML = `
      (function(w,d,s,l,i){
        w[l]=w[l]||[];w[l].push({'gtm.start':new Date().getTime(),event:'gtm.js'});
        var f=d.getElementsByTagName(s)[0],
            j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';
        j.async=true; j.src='https://www.googletagmanager.com/gtm.js?id='+i+dl;
        f.parentNode.insertBefore(j,f);
      })(window,document,'script','dataLayer','${gtmId}');
    `
        document.head.appendChild(script)
    }

    const loadNoscriptGTM = () => {
        if (document.getElementById("gtm-noscript")) return

        const gtmIdNoscript = isLandingPage
            ? GTM_IDS["lp"]
            : GTM_IDS[localePrefix] || GTM_IDS["in/en"]
        if (!gtmIdNoscript) return

        const noscript = document.createElement("noscript")
        noscript.id = "gtm-noscript"
        noscript.innerHTML = `
        <iframe src="https://www.googletagmanager.com/ns.html?id=${gtmIdNoscript}"
        height="0" width="0" style="display:none;visibility:hidden"></iframe>
      `
        document.body.appendChild(noscript)
    }

    const loadRUM = () => {
        if (
            typeof window === "undefined" ||
            typeof document === "undefined" ||
            !RUM_SERVER ||
            !RUM_SITE_ID
        ) {
            return null
        }

        if (document.getElementById("digitup-rum")) return

        const script = document.createElement("script")
        script.id = "digitup-rum"
        script.src = `${RUM_SERVER}`
        script.defer = true

        script.setAttribute("data-site-id", RUM_SITE_ID)
        script.setAttribute("data-brand", RUM_BRAND ?? "")
        script.setAttribute("data-market", "India")

        document.head.appendChild(script)
    }

    const loadCWV = () => {
        if (document.getElementById("cwv-script")) return

        const script = document.createElement("script")
        script.id = "cwv-script"
        script.async = true
        script.src = "https://dc-backend-74354760757.asia-south1.run.app/api/v1/ingest/rum/dc-rum.js"
        script.setAttribute("data-token", "dc-data-digitup123456")
        script.setAttribute("data-ingest", "https://dc-backend-74354760757.asia-south1.run.app/api/v1/ingest/cwv")
        script.setAttribute("data-debug", "true")

        document.head.appendChild(script)
    }

    // NEW — DigitQ Heatmap Scripts
    const loadDigitQHeatmap = () => {
        if (document.getElementById("digitq-section-storage")) return

        const script1 = document.createElement("script")
        script1.id = "digitq-section-storage"
        script1.src =
            "https://heatmap-preview.digitq.in/sections/sectionStorage.js"
        script1.defer = true

        const script2 = document.createElement("script")
        script2.id = "digitq-event-tracking"
        script2.src =
            "https://heatmap-preview.digitq.in/eventTracking/eventTracking.js"
        script2.defer = true

        document.head.appendChild(script1)
        document.head.appendChild(script2)
    }

    useEffect(() => {
        if (typeof window === "undefined") {
            return undefined
        }

        const events = [
            "keydown",
            "touchstart",
            "click",
        ]

        const loadMeasurementScripts = () => {
            if (hasLoadedRef.current) return

            hasLoadedRef.current = true

            loadRUM()
            loadCWV()

            window.setTimeout(() => {
                loadGTM({ localePrefix, isLandingPage })
                loadNoscriptGTM()
            }, 12000)

            if (isProduction) {
                window.setTimeout(loadDigitQHeatmap, 30000)
            }
        }

        const scheduleLoad = (timeout = 5000) => {
            if ("requestIdleCallback" in window) {
                window.requestIdleCallback(loadMeasurementScripts, { timeout })
                return
            }

            window.setTimeout(loadMeasurementScripts, 500)
        }

        const handleEvent = () => {
            events.forEach(e => window.removeEventListener(e, handleEvent))
            scheduleLoad()
        }

        events.forEach(e =>
            window.addEventListener(e, handleEvent, { passive: true })
        )

        const timer = setTimeout(() => {
            scheduleLoad(10000)
        }, 20000)

        return () => {
            clearTimeout(timer)
            events.forEach(e => window.removeEventListener(e, handleEvent))
        }
    }, [localePrefix, isLandingPage])

    return null
}

export default Scripts
